/// Роли і права. SPEC: Zala Handoff Spec §6.
///
/// Правило екрана, не тільки даних: **недоступна дія не показується взагалі**.
/// Жодних кнопок, що ведуть у 403. Виняток один — дії, для яких передбачено
/// виклик менеджера (зняття блоку): вони видимі, неактивні, і кажуть, чиє це
/// право.
///
/// RBAC у хендофі стоїть в етапі 1 саме тому, що пізніше він не додається:
/// права мають бути в основі кожного екрана, а не накладені зверху.

import type { Role } from './types'

export const CAPABILITIES = [
  'checkin',
  'sell',
  'book',
  'client_read',
  'client_write',
  /// D6. Масова дія по сегменту і вивантаження списку — власні права, а не
  /// «найближчі за сенсом»: помилка в масовій дії коштує триста людей, а не
  /// одну картку, і роль, якій дозволили редагувати картку, не мусить
  /// автоматично мати це.
  'client_bulk',
  'client_export',
  'own_shift',
  'refund',
  'sell_on_debt',
  'override_block',
  'discount_over_15',
  'cash_discrepancy',
  'schedule_manage',
  'pass_builder',
  'own_roster',
  'mark_attendance',
  'reports_all_branches',
  'club_settings',
  'payroll',
] as const

export type Capability = (typeof CAPABILITIES)[number]

/// Рецепція: щоденна робота з ОДНІЄЮ людиною за раз.
///
/// `client_bulk` і `client_export` тут НЕ стоять — це рішення D6, і воно було
/// записане з причиною: помилка в масовій дії коштує триста людей, а не одну
/// картку, а вивантаження списку — це персональні дані всієї бази на чиємусь
/// ноутбуці. Обидва рядки колись потрапили саме сюди разом зі скопійованим
/// коментарем про D6 — і продублювались нижче в `MANAGER`, який і так робить
/// `...RECEPTIONIST`. Дублікат і був доказом, що блок вставили не в ту роль.
const RECEPTIONIST: Capability[] = [
  'checkin',
  'sell',
  'book',
  'client_read',
  'client_write',
  'own_shift',
]

/// Менеджер — це рецепціоніст плюс усе, що пише в журнал: повернення, борг,
/// зняття блоку, знижка понад 15%, акт розбіжності.
const MANAGER: Capability[] = [
  ...RECEPTIONIST,
  'client_bulk',
  'client_export',
  'refund',
  'sell_on_debt',
  'override_block',
  'discount_over_15',
  'cash_discrepancy',
  'schedule_manage',
  'pass_builder',
]

/// Тренер — свій розклад і ростер, відмітка приходу. БЕЗ грошей: у нього немає
/// жодної capability, що читає суми.
/// Чи ця роль узагалі читає СУМИ.
///
/// Правило «тренер без грошей» декларувалось у коментарі, а перевірялось у
/// кожного екрана окремо — тож трималось рівно там, де про нього згадали:
/// картка клієнта ховала від тренера блок «Гроші» і за двадцять пікселів
/// друкувала ті самі суми у стрічці подій, а список клієнтів — колонку «Борг».
///
/// Тепер питання одне на застосунок. `sell` тут не «має право продавати», а
/// «має справу з грошима»: саме її немає в тренера, і саме на неї вже спирався
/// блок «Гроші» на картці (D70).
export const readsMoney = (role: Role): boolean => can(role, 'sell')

const TRAINER: Capability[] = ['own_roster', 'mark_attendance', 'client_read']

const OWNER: Capability[] = [...MANAGER, 'reports_all_branches', 'club_settings', 'payroll']

const GRANTS: Readonly<Record<Role, readonly Capability[]>> = {
  receptionist: RECEPTIONIST,
  manager: MANAGER,
  trainer: TRAINER,
  owner: OWNER,
}

export const can = (role: Role, capability: Capability): boolean =>
  GRANTS[role].includes(capability)

export const capabilitiesOf = (role: Role): readonly Capability[] => GRANTS[role]

/// Хто МОЖЕ це зробити — для підпису на неактивній дії: «зняти блок може
/// менеджер». Без цього рецепціоніст бачить сіру кнопку і не знає, кого звати.
export const rolesWith = (capability: Capability): Role[] =>
  (Object.keys(GRANTS) as Role[]).filter((role) => can(role, capability))

export const ROLE_LABEL: Record<Role, string> = {
  receptionist: 'Рецепціоніст',
  manager: 'Менеджер',
  trainer: 'Тренер',
  owner: 'Власник',
}

/// «потрібна роль Менеджер або Власник» — з ПРАВА, а не з памʼяті (D105).
///
/// Цей рядок був написаний руками у двох діалогах: боргу на касі й
/// переоформлення абонемента. Обидва казали «потрібна роль Менеджер», хоча
/// право `pay_debt` має ще й Власник — тобто підказка вже тоді була неповна, а
/// після першої ж правки прав стала б просто хибною: кнопка розблокується, а
/// текст під нею й далі назве стару роль.
///
/// Панель масових дій це вміє давно (`rolesWith` + «— право: …»); тепер тим
/// самим користуються і діалоги.
/// Хто МОЖЕ це зробити — підпис під неактивною дією («Менеджер або Власник»).
///
/// Був сімома копіями по фічах, і дві з них розходились роздільником: та сама
/// відповідь читалась «Менеджер, Власник» у панелі масових дій і «Менеджер або
/// Власник» у діалогах (D107). Роздільник — це рішення, і приймається воно один
/// раз: перелік ролей — диз'юнкція, досить будь-якої.
export const roleNames = (capability: Capability): string =>
  rolesWith(capability)
    .map((role) => ROLE_LABEL[role])
    .join(' або ')

/// «право: Менеджер або Власник» — готова прикладка для підпису дії (D108).
///
/// Двокрапка тут не оформлення, а граматика. Підписи казали «право Менеджер або
/// Власник» — називний відмінок після слова, яке вимагає родового («право
/// Менеджера»). Відмінювати назви ролей у коді не можна: `ROLE_LABEL` — це
/// словник, а не граматика, і кожна нова роль тягла б за собою другу форму.
/// Двокрапка знімає питання відмінка взагалі — і саме її вже вживає панель
/// масових дій.
export const rightNote = (capability: Capability): string =>
  `право: ${roleNames(capability)}`

export const needRole = (capability: Capability): string =>
  `потрібна роль ${roleNames(capability)}`
